import _ from 'lodash';
import moment from 'moment';
import React, { useContext, useState, useEffect } from 'react';
import Dialog from 'src/components/common/Dialog.redesign';
import Button from 'src/components/controls/forms/redesign/FormButton';
import ContractOwnershipList from 'src/models/ContractOwnershipList';
import LandContractType from 'src/models/LandContractType';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import useFetch from 'use-http';
import { isDate } from 'util';

export interface IDeleteOwnershipDialogProps {
    navigate: (path: string) => void;
    confirmDelete: () => void;
    onCloseDialog?: () => void;
}

export type IDeleteOwnershipDialogStateProps = {
    ownershipId: number;
};

type IDeleteOwnershipDialogPageProps = IDeleteOwnershipDialogProps & IDeleteOwnershipDialogStateProps;

const DeleteOwnershipDialog = (props: IDeleteOwnershipDialogPageProps) => {
    const appContext = useContext<IAppContextState>(AppContext);
    const { get, del, response } = useFetch();
    const [leases, setLeases] = useState<Array<ContractOwnershipList>>(null);
    const [leasesFound, setLeasesFound] = useState<boolean>(false);

    const getLeases = async () => {
        const result = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/ownershipId/${props.ownershipId}/contracts/false`
        );

        if (response.ok) {
            setLeases(result);
            setLeasesFound(true);
        }
    };

    useEffect(() => {
        setLeasesFound(false);
        if (props.ownershipId > 0) {
            getLeases();
        }
    }, [props.ownershipId]);

    const thirdPartyCard = (lease: ContractOwnershipList, nav: boolean) => {
        // TODO: Add delete button.
        // Trigger should be the same as "Delete Leasehold call" on that page.

        return (
            <div
                style={{ backgroundColor: 'white', borderBottom: '1px solid grey', padding: '.5rem', display: 'flex' }}
                className={nav ? 'clickable' : 'undefined'}
            >
                <div style={{ width: '14%' }}>{lease.agreementNumber || 'N/A'}</div>

                <div style={{ width: '10%' }}>{`${lease.book} / ${lease.page}`}</div>

                <div style={{ width: '10%' }}>{isDate(lease.agreementDate) ? moment(lease.agreementDate)?.format('MM/DD/YYYY') : 'N/A'}</div>

                <div style={{ width: '21%' }}>{lease.originalLessor || 'N/A'}</div>

                <div style={{ width: '21%' }}>{lease.originalLessee || 'N/A'}</div>

                <div style={{ width: '14%' }}>Active</div>
                <Button text="Delete" onClick={() => confirmDeleteLeasehold(lease.agreementholdId, lease.agreementType)} intent="danger" />
            </div>
        );
    };

    const roughDraftCard = (lease: ContractOwnershipList, nav: boolean) => {
        // TODO: Add 'Detach Owner' button.
        // Trigger should be the same as deselecting a Lease Tract from that Contract.

        return (
            <div
                style={{ backgroundColor: 'white', borderBottom: '1px solid grey', padding: '.5rem', display: 'flex' }}
                className={nav ? 'clickable' : 'undefined'}
            >
                <div style={{ width: '14%' }}>{lease.agreementNumber || 'N/A'}</div>

                <div style={{ width: '10%' }}>{`${lease.book} / ${lease.page}`}</div>

                <div style={{ width: '10%' }}>{moment(lease.agreementDate)?.format('MM/DD/YYYY') || 'N/A'}</div>

                <div style={{ width: '21%' }}>{lease.originalLessor || 'N/A'}</div>

                <div style={{ width: '21%' }}>{lease.originalLessee || 'N/A'}</div>

                <div style={{ width: '14%' }}>Active</div>
                <Button text="Detach" onClick={() => confirmDetachLeasehold(lease.agreementholdId, lease.agreementType)} intent="danger" />
            </div>
        );
    };

    const inHandedCard = (lease: ContractOwnershipList, nav: boolean) => {
        return (
            <div
                style={{ backgroundColor: 'white', borderBottom: '1px solid grey', padding: '.5rem', display: 'flex' }}
                className={nav ? 'clickable' : 'undefined'}
            >
                <div style={{ width: '14%' }}>{lease.agreementNumber || 'N/A'}</div>

                <div style={{ width: '10%' }}>{`${lease.book} / ${lease.page}`}</div>

                <div style={{ width: '10%' }}>{moment(lease.agreementDate)?.format('MM/DD/YYYY') || 'N/A'}</div>

                <div style={{ width: '21%' }}>{lease.originalLessor || 'N/A'}</div>

                <div style={{ width: '21%' }}>{lease.originalLessee || 'N/A'}</div>

                <div style={{ width: '14%' }}>Active</div>
            </div>
        );
    };

    const confirmDelete = () => {
        props.confirmDelete();
        props.onCloseDialog();
    };

    const confirmDeleteLeasehold = async (leaseholdId: number, agreementType: string) => {
        let requestPath: string;

        switch (agreementType) {
            case 'Lease':
                // tslint:disable-next-line:max-line-length
                requestPath = `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/${appContext.tractNumber}/ownership/${props.ownershipId}/leasehold/${leaseholdId}/false`;
                break;
            case 'Solar Lease':
                // tslint:disable-next-line:max-line-length
                requestPath = `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${props.ownershipId}/solarLease/${leaseholdId}/false`;
                break;
            case 'Surface Waiver':
                // tslint:disable-next-line:max-line-length
                requestPath = `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${props.ownershipId}/surfaceWaiver/${leaseholdId}/false`;
                break;
            case 'Battery Storage':
                // tslint:disable-next-line:max-line-length
                requestPath = `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${props.ownershipId}/batteryStorage/${leaseholdId}/false`;
                break;
            case 'Brine Lease':
                // tslint:disable-next-line:max-line-length
                requestPath = `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${props.ownershipId}/brineLease/${leaseholdId}/false`;
                break;
            default:
                return;
        }
        await del(requestPath);
        if (response.ok) {
            handleDeleteLeasehold(leaseholdId);
        } else {
            window.alert('delete leasehold failed');
        }
    };

    const handleDeleteLeasehold = (leaseholdId: number) => {
        let newList = [...leases];
        newList.splice(newList.indexOf(newList.find(nl => nl.agreementholdId === leaseholdId)), 1);
        setLeases(newList);
    };

    const confirmDetachLeasehold = async (leaseholdId: number, agreementType: string) => {
        let contractType: LandContractType;

        switch (agreementType) {
            case 'Lease':
                contractType = LandContractType.OilAndGas;
                break;
            case 'Solar Lease':
                contractType = LandContractType.SolarLease;
                break;
            case 'Surface Waiver':
                contractType = LandContractType.SurfaceWaiver;
                break;
            case 'Battery Storage':
                contractType = LandContractType.BatteryStorage;
                break;
            case 'Brine Lease':
                contractType = LandContractType.BrineLease;
                break;
            default:
                return;
        }

        await del(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contractGeneration/leasehold/${leaseholdId}/${contractType}`);

        if (response.ok) {
            handleDeleteLeasehold(leaseholdId);
        } else {
            window.alert('detach leasehold failed');
        }
    };

    const leaseholdList = () => {
        if (leases?.length > 0) {
            let thirdParty = leases.filter(ls => ls.isGeneratedAgreement !== true);
            let draftLeases = leases.filter(ls => ls.isGeneratedAgreement === true && ls.isInHand !== true);
            let inHandLeases = leases.filter(ls => ls.isGeneratedAgreement === true && ls.isInHand === true);

            return (
                <div style={{ marginBottom: '1rem', width: '60vw' }}>
                    <div>Please address attached leases before deleting ownership.</div>
                    <div>
                        <fieldset style={{ padding: '.3rem 0', borderWidth: '1px 0 0 0' }}>
                            <legend className="legend">Attached Leases</legend>
                        </fieldset>
                    </div>

                    <div style={{ display: 'flex', padding: '.5rem' }}>
                        <div style={{ width: '14%' }}>Lease Number</div>
                        <div style={{ width: '14%' }}>Vol/Page | Inst#</div>
                        <div style={{ width: '14%' }}>Lease Date</div>
                        <div style={{ width: '21%' }}>Lessor</div>
                        <div style={{ width: '21%' }}>Lessee</div>
                        <div style={{ width: '14%' }}>Status</div>
                    </div>

                    {thirdParty?.length > 0 && (
                        <div>
                            <span>Third Party leaseholds:</span>
                            {thirdParty.map((lease: ContractOwnershipList) => { return thirdPartyCard(lease, true); })}
                        </div>
                    )}
                    {draftLeases?.length > 0 && (
                        <div>
                            <span>Rough Draft Leaseholds:</span>
                            {draftLeases.map((lease: ContractOwnershipList) => { return roughDraftCard(lease, true); })}
                        </div>
                    )}
                    {inHandLeases?.length > 0 && (
                        <div>
                            <span style={{ color: 'red' }}>The following leaseholds are in-Handed. Please contact your system admin to request removal.</span>
                            {inHandLeases.map((lease: ContractOwnershipList) => { return inHandedCard(lease, true); })}
                        </div>
                    )}
                    <div className="mt-2 button-row">
                        <Button text="Cancel" onClick={props.onCloseDialog} />
                    </div>
                </div>
            );
        }
        return (
            <>
                <div>Are you sure that you want to permanently delete the selected ownership?</div>
                <div className="mt-2 button-row">
                    <Button text="OK" onClick={confirmDelete} intent="primary" />
                    <Button text="Cancel" onClick={props.onCloseDialog} />
                </div>
            </>
        );
    };

    return (
        <Dialog title="Confirm Delete Ownership">
            <div>
                {leasesFound && (leaseholdList())}
                {!leasesFound && (<div>Checking for leases...</div>)}
            </div>
        </Dialog>
    );
};

export default DeleteOwnershipDialog;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/DeleteOwnershipDialog.tsx